import {StrictMode} from 'react';
import {createRoot} from 'react-dom/client';
import {BrowserRouter} from 'react-router-dom';
import {Auth0Provider} from '@auth0/auth0-react';
import {ThemeProvider} from 'next-themes';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {TooltipProvider} from '@kroll-resolver-crisp/ui-components-react';
import {AuthProvider} from './context/AuthContext';
import {applyStoredAppearance} from './lib/appearance';
import {App} from './App';
import './index.css';

const root = document.querySelector('#root');
if (!root) {
  throw new Error('Root element #root not found');
}

const queryClient = new QueryClient();

// Vite's BASE_URL always carries a trailing slash; React Router wants the basename without it.
const base = import.meta.env.BASE_URL;
const auth0Domain = import.meta.env.VITE_AUTH0_DOMAIN;
const auth0ClientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
const auth0Audience = import.meta.env.VITE_AUTH0_AUDIENCE;
if (!auth0Domain || !auth0ClientId || !auth0Audience) {
  throw new Error(
    'VITE_AUTH0_DOMAIN, VITE_AUTH0_CLIENT_ID, and VITE_AUTH0_AUDIENCE must be configured.'
  );
}

const routerBase = base.endsWith('/') ? base.slice(0, -1) : base;

const app = (
  <StrictMode>
    <Auth0Provider
      useRefreshTokens
      domain={auth0Domain}
      clientId={auth0ClientId}
      cacheLocation="localstorage"
      authorizationParams={{
        audience: auth0Audience,
        redirect_uri: `${globalThis.location.origin}${base}`,
        scope: 'openid profile email offline_access'
      }}>
      {/* The design system keys dark mode on the `.dark` class (ui-tokens defines the `dark:`
          variant against it), so the attribute is "class" - next-themes' own default. */}
      <ThemeProvider
        enableSystem
        disableTransitionOnChange
        attribute="class"
        defaultTheme="system"
        storageKey="theme">
        <QueryClientProvider client={queryClient}>
          <BrowserRouter basename={routerBase}>
            <AuthProvider>
              {/* Tooltips are used across the app; the provider must wrap every page that renders one. */}
              <TooltipProvider>
                <App />
              </TooltipProvider>
            </AuthProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </ThemeProvider>
    </Auth0Provider>
  </StrictMode>
);

// Accent and font size go on <html> before the first render - see lib/appearance.ts for why this,
// rather than an inline script in index.html, is early enough.
applyStoredAppearance();
createRoot(root).render(app);
